import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [resultsPublished, setResultsPublished] = useState(false);
  const [hasPaid, setHasPaid] = useState(false);
  const [editionEnded, setEditionEnded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    checkAuthStatus();
    checkResultsPublished();
    checkEditionStatus();
  }, []);

  const checkAuthStatus = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setIsLoggedIn(!!session);

    if (session) {
      // Vérifier si l'utilisateur a payé
      const { data } = await supabase
        .from('registrations')
        .select('payment_status')
        .eq('user_id', session.user.id)
        .eq('payment_status', 'completed')
        .single();

      setHasPaid(!!data);
    }
  };

  const checkResultsPublished = async () => {
    try {
      const { data, error } = await supabase
        .from('countdown_settings')
        .select('results_published')
        .single();

      if (!error && data) {
        setResultsPublished(data.results_published || false);
      }
    } catch (error) {
      console.error('Erreur lors de la vérification des résultats:', error);
    }
  };

  const checkEditionStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('countdown_settings')
        .select('edition_ended')
        .single();

      if (!error && data) {
        setEditionEnded(data.edition_ended || false);
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du statut de l\'édition:', error);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsLoggedIn(false);
    setHasPaid(false);
    navigate('/');
    setIsMenuOpen(false);
  };

  const handleCallReservation = () => {
    if (editionEnded) {
      alert('L\'édition 2026 est terminée. Les chats vocaux ne sont plus disponibles.');
      return;
    }
    navigate('/chat-vocal');
  };

  const isHomePage = location.pathname === '/';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHomePage ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-white/10 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/9e7578784cafb125cff30a4f31132a63/ad27ada49540da22387d47bc2e323587.png"
              alt="Tulaura"
              className="h-8 sm:h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-4 xl:gap-8">
            <Link
              to="/"
              className={`font-semibold transition-colors whitespace-nowrap cursor-pointer text-sm xl:text-base ${
                isScrolled || !isHomePage ? 'text-gray-700 hover:text-secondary' : 'text-white hover:text-accent'
              }`}
            >
              Accueil
            </Link>
            <Link
              to="/comment-ca-marche"
              className={`font-semibold transition-colors whitespace-nowrap cursor-pointer text-sm xl:text-base ${
                isScrolled || !isHomePage ? 'text-gray-700 hover:text-secondary' : 'text-white hover:text-accent'
              }`}
            >
              Comment ça marche
            </Link>
            <Link
              to="/projets"
              className={`font-semibold transition-colors whitespace-nowrap cursor-pointer text-sm xl:text-base ${
                isScrolled || !isHomePage ? 'text-gray-700 hover:text-secondary' : 'text-white hover:text-accent'
              }`}
            >
              Projets
            </Link>
            <Link
              to="/tous-les-projets"
              className={`font-semibold transition-colors whitespace-nowrap cursor-pointer text-sm xl:text-base ${
                isScrolled || !isHomePage ? 'text-gray-700 hover:text-secondary' : 'text-white hover:text-accent'
              }`}
            >
              Tous les projets
            </Link>
            {resultsPublished && (
              <Link
                to="/resultats"
                className={`font-semibold transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2 text-sm xl:text-base ${
                  isScrolled || !isHomePage ? 'text-gray-700 hover:text-secondary' : 'text-white hover:text-accent'
                }`}
              >
                <i className="ri-trophy-line"></i>
                Résultats
              </Link>
            )}
            <Link
              to="/reglement"
              className={`font-semibold transition-colors whitespace-nowrap cursor-pointer text-sm xl:text-base ${
                isScrolled || !isHomePage ? 'text-gray-700 hover:text-secondary' : 'text-white hover:text-accent'
              }`}
            >
              Règlement
            </Link>
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-2 xl:gap-4">
            {isHomePage && (
              <button
                onClick={handleCallReservation}
                className={`inline-flex items-center justify-center w-9 h-9 xl:w-10 xl:h-10 rounded-full transition-all whitespace-nowrap cursor-pointer text-sm xl:text-base ${
                  isScrolled || !isHomePage
                    ? 'bg-secondary/10 text-secondary hover:bg-secondary hover:text-white'
                    : 'bg-white/20 text-white hover:bg-white hover:text-secondary'
                } ${!hasPaid || editionEnded ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={editionEnded ? 'Édition terminée' : !hasPaid ? 'Réservé aux participants ayant payé' : 'Chat vocal'}
                disabled={!hasPaid || editionEnded}
              >
                <i className="ri-mic-line text-lg xl:text-xl"></i>
              </button>
            )}
            {isLoggedIn ? (
              <button
                onClick={handleLogout}
                className={`px-4 xl:px-6 py-2 xl:py-2.5 font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer text-xs xl:text-sm shadow-[inset_2px_2px_2px_0px_rgba(255,255,255,.5),7px_7px_20px_0px_rgba(0,0,0,.1),4px_4px_5px_0px_rgba(0,0,0,.1)] hover:shadow-[4px_4px_6px_0_rgba(255,255,255,.5),-4px_-4px_6px_0_rgba(116,125,136,.5),inset_-4px_-4px_6px_0_rgba(255,255,255,.2),inset_4px_4px_6px_0_rgba(0,0,0,.4)] ${
                  isScrolled || !isHomePage
                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    : 'bg-white/20 text-white hover:bg-white hover:text-secondary backdrop-blur-sm'
                }`}
              >
                Déconnexion
              </button>
            ) : (
              <>
                <Link
                  to="/connexion"
                  className={`px-4 xl:px-6 py-2 xl:py-2.5 font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer text-xs xl:text-sm shadow-[inset_2px_2px_2px_0px_rgba(255,255,255,.5),7px_7px_20px_0px_rgba(0,0,0,.1),4px_4px_5px_0px_rgba(0,0,0,.1)] hover:shadow-[4px_4px_6px_0_rgba(255,255,255,.5),-4px_-4px_6px_0_rgba(116,125,136,.5),inset_-4px_-4px_6px_0_rgba(255,255,255,.2),inset_4px_4px_6px_0_rgba(0,0,0,.4)] ${
                    isScrolled || !isHomePage
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-white/20 text-white hover:bg-white hover:text-secondary backdrop-blur-sm'
                  }`}
                >
                  Connexion
                </Link>
                <Link
                  to="/registration"
                  className="px-4 xl:px-6 py-2 xl:py-2.5 bg-gradient-to-r from-accent to-accent text-white font-bold rounded-lg transition-all whitespace-nowrap cursor-pointer text-xs xl:text-sm shadow-[inset_2px_2px_2px_0px_rgba(255,255,255,.5),7px_7px_20px_0px_rgba(0,0,0,.1),4px_4px_5px_0px_rgba(0,0,0,.1)] hover:shadow-[4px_4px_6px_0_rgba(255,255,255,.5),-4px_-4px_6px_0_rgba(116,125,136,.5),inset_-4px_-4px_6px_0_rgba(255,255,255,.2),inset_4px_4px_6px_0_rgba(0,0,0,.4)]"
                >
                  Participer
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors cursor-pointer ${
              isScrolled || !isHomePage ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'
            }`}
            aria-label="Menu"
          >
            <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 py-4 shadow-lg">
            <nav className="flex flex-col gap-2">
              <Link
                to="/"
                onClick={() => setIsMenuOpen(false)}
                className="px-4 py-3 text-gray-700 hover:bg-gray-50 font-semibold transition-colors cursor-pointer text-sm"
              >
                Accueil
              </Link>
              <Link
                to="/comment-ca-marche"
                onClick={() => setIsMenuOpen(false)}
                className="px-4 py-3 text-gray-700 hover:bg-gray-50 font-semibold transition-colors cursor-pointer text-sm"
              >
                Comment ça marche
              </Link>
              <Link
                to="/projets"
                onClick={() => setIsMenuOpen(false)}
                className="px-4 py-3 text-gray-700 hover:bg-gray-50 font-semibold transition-colors cursor-pointer text-sm"
              >
                Projets
              </Link>
              <Link
                to="/tous-les-projets"
                onClick={() => setIsMenuOpen(false)}
                className="px-4 py-3 text-gray-700 hover:bg-gray-50 font-semibold transition-colors cursor-pointer text-sm"
              >
                Tous les projets
              </Link>
              {resultsPublished && (
                <Link
                  to="/resultats"
                  onClick={() => setIsMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gray-50 font-semibold transition-colors cursor-pointer flex items-center gap-2 text-sm"
                >
                  <i className="ri-trophy-line"></i>
                  Résultats
                </Link>
              )}
              <Link
                to="/reglement"
                onClick={() => setIsMenuOpen(false)}
                className="px-4 py-3 text-gray-700 hover:bg-gray-50 font-semibold transition-colors cursor-pointer text-sm"
              >
                Règlement
              </Link>
              {isHomePage && (
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    handleCallReservation();
                  }}
                  className={`px-4 py-3 text-secondary hover:bg-secondary/10 font-semibold transition-colors cursor-pointer flex items-center gap-2 text-left text-sm ${
                    !hasPaid || editionEnded ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={!hasPaid || editionEnded}
                >
                  <i className="ri-mic-line"></i>
                  Chat vocal
                </button>
              )}
              <div className="border-t border-gray-100 mt-2 pt-2">
                {isLoggedIn ? (
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 font-semibold transition-colors cursor-pointer text-sm"
                  >
                    Déconnexion
                  </button>
                ) : (
                  <>
                    <Link
                      to="/connexion"
                      onClick={() => setIsMenuOpen(false)}
                      className="block px-4 py-3 text-gray-700 hover:bg-gray-50 font-semibold transition-colors cursor-pointer text-sm"
                    >
                      Connexion
                    </Link>
                    <Link
                      to="/registration"
                      onClick={() => setIsMenuOpen(false)}
                      className="block px-4 py-3 text-accent hover:bg-accent/10 font-bold transition-colors cursor-pointer text-sm"
                    >
                      Participer
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
